import React, { useState, useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import useXHR from '../../hooks/useXHR';
import { API } from '../../utils/api';
import 'leaflet/dist/leaflet.css';

// Fix for leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

/**
 * DatabaseToggle - Toggle between Personal, Individual, and Worldwide databases
 */
export const DatabaseToggle = ({ value, onChange, userList = [], onUserSelect }) => {
  const { get, post, del: xhrDel, put } = useXHR(token);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const options = [
    { id: 'personal', label: 'Personal', icon: '👤', description: 'Your data only' },
    { id: 'individual', label: 'Individual', icon: '👥', description: 'View others\' public data' },
    { id: 'worldwide', label: 'Worldwide', icon: '🌍', description: 'All public data' }
  ];
  
  return (
    <div style={{ 
      background: 'rgba(30, 41, 59, 0.95)', 
      borderRadius: 12, 
      padding: 12,
      marginBottom: 12,
      border: '1px solid rgba(252, 211, 77, 0.3)'
    }}>
      <div style={{ 
        fontSize: '0.75rem', 
        color: '#a1a1aa', 
        marginBottom: 8,
        fontWeight: 600 
      }}>
        Database Source
      </div>
      
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {options.map(opt => (
          <button
            key={opt.id}
            onClick={() => {
              onChange(opt.id);
              if (opt.id !== 'individual') setSelectedUser(null);
            }}
            data-testid={`db-toggle-${opt.id}`}
            style={{
              flex: 1,
              minWidth: 80,
              padding: '8px 10px',
              borderRadius: 8,
              border: value === opt.id 
                ? '2px solid #FCD34D' 
                : '1px solid rgba(255,255,255,0.1)',
              background: value === opt.id 
                ? 'linear-gradient(135deg, rgba(252, 211, 77, 0.3), rgba(253, 224, 71, 0.2))' 
                : 'rgba(255, 255, 255, 0.85)',
              color: value === opt.id ? '#047857' : '#9ca3af',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>{opt.icon}</span>
            <span>{opt.label}</span>
          </button>
        ))}
      </div>
      
      {/* User selector for Individual mode */}
      {value === 'individual' && userList.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: 6 }}>
            Click a username to view their public data:
          </div>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 6,
            maxHeight: 100,
            overflowY: 'auto'
          }}>
            {userList.map(u => (
              <button
                key={u.id}
                onClick={() => {
                  setSelectedUser(u.id);
                  onUserSelect && onUserSelect(u.id);
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: 15,
                  border: selectedUser === u.id 
                    ? '1px solid #10b981' 
                    : '1px solid rgba(255,255,255,0.1)',
                  background: selectedUser === u.id 
                    ? 'rgba(16, 185, 129, 0.2)' 
                    : 'rgba(255, 255, 255, 0.85)',
                  color: selectedUser === u.id ? '#10b981' : '#9ca3af',
                  fontSize: '0.7rem',
                  cursor: 'pointer'
                }}
              >
                @{u.username || u.email?.split('@')[0]}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * CategoryCheckboxes - Checkboxes for category selection
 */
export const CategoryCheckboxes = ({ categories, selectedIds, onChange }) => {
  const handleToggle = (catId) => {
    if (selectedIds.includes(catId)) {
      onChange(selectedIds.filter(id => id !== catId));
    } else {
      onChange([...selectedIds, catId]);
    }
  };
  
  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.95)',
      borderRadius: 12,
      padding: 12,
      marginBottom: 12,
      border: '1px solid rgba(245, 158, 11, 0.3)'
    }}>
      <div style={{ 
        fontSize: '0.75rem', 
        color: '#a1a1aa', 
        marginBottom: 8,
        fontWeight: 600 
      }}>
        Categories ({categories.length})
      </div>
      
      <div style={{ 
        maxHeight: 150, 
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 4
      }}>
        {categories.map(cat => (
          <label 
            key={cat.id || cat._id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 8px',
              borderRadius: 6,
              cursor: 'pointer',
              background: selectedIds.includes(cat.id || cat._id) 
                ? 'rgba(245, 158, 11, 0.15)' 
                : 'transparent',
              transition: 'background 0.2s'
            }}
          >
            <input
              type="checkbox"
              checked={selectedIds.includes(cat.id || cat._id)}
              onChange={() => handleToggle(cat.id || cat._id)}
              style={{ 
                accentColor: '#3b82f6',
                width: 14,
                height: 14
              }}
            />
            <span style={{ 
              fontSize: '0.75rem', 
              color: '#334155',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {cat.name}
            </span>
            {cat.result_count > 0 && (
              <span style={{ 
                fontSize: '0.6rem', 
                color: '#6b7280',
                background: 'rgba(255,255,255,0.1)',
                padding: '2px 6px',
                borderRadius: 10
              }}>
                {cat.result_count}
              </span>
            )}
          </label>
        ))}
        
        {categories.length === 0 && (
          <div style={{ 
            color: '#6b7280', 
            fontSize: '0.75rem', 
            textAlign: 'center',
            padding: 10 
          }}>
            No categories found
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * URLPreferenceFilters - Content type filters
 */
export const URLPreferenceFilters = ({ filters, onChange }) => {
  const filterOptions = [
    { id: 'all', label: 'All Types' },
    { id: 'news', label: 'News Articles' },
    { id: 'blog', label: 'Blog Posts' },
    { id: 'social', label: 'Social Media' },
    { id: 'video', label: 'Videos' },
    { id: 'academic', label: 'Academic' },
    { id: 'government', label: 'Government' }
  ];
  
  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.95)',
      borderRadius: 12,
      padding: 12,
      marginBottom: 12,
      border: '1px solid rgba(16, 185, 129, 0.3)'
    }}>
      <div style={{ 
        fontSize: '0.75rem', 
        color: '#a1a1aa', 
        marginBottom: 8,
        fontWeight: 600 
      }}>
        Content Type Filters
      </div>
      
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {filterOptions.map(opt => (
          <label 
            key={opt.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 15,
              cursor: 'pointer',
              background: filters.includes(opt.id) 
                ? 'rgba(16, 185, 129, 0.2)' 
                : 'rgba(255, 255, 255, 0.85)',
              border: filters.includes(opt.id)
                ? '1px solid rgba(16, 185, 129, 0.5)'
                : '1px solid rgba(255,255,255,0.1)',
              transition: 'all 0.2s'
            }}
          >
            <input
              type="checkbox"
              checked={filters.includes(opt.id)}
              onChange={() => {
                if (filters.includes(opt.id)) {
                  onChange(filters.filter(f => f !== opt.id));
                } else {
                  onChange([...filters, opt.id]);
                }
              }}
              style={{ 
                accentColor: '#10b981',
                width: 12,
                height: 12
              }}
            />
            <span style={{ 
              fontSize: '0.7rem', 
              color: filters.includes(opt.id) ? '#10b981' : '#9ca3af'
            }}>
              {opt.label}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

/**
 * InteractiveMapPanel - Interactive map with markers for search results
 */
export const InteractiveMapPanel = ({ 
  results = [], 
  center = [39.8283, -98.5795], 
  zoom = 4,
  height = 300,
  onMarkerClick
}) => {
  // Get marker color based on result type
  const getMarkerColor = (result) => {
    const type = result.article_type?.toLowerCase() || '';
    if (type.includes('news')) return '#3b82f6';
    if (type.includes('blog')) return '#10b981';
    if (type.includes('social')) return '#FEF08A';
    if (type.includes('video')) return '#ef4444';
    if (type.includes('academic')) return '#FDE047';
    return '#fbbf24';
  };
  
  // Filter results with valid coordinates
  const locatedResults = results.filter(r => r.latitude && r.longitude);
  
  // Expand results with all_locations to create multiple markers per result
  const allMarkers = [];
  results.forEach(result => {
    if (result.all_locations && result.all_locations.length > 0) {
      result.all_locations.forEach((loc, idx) => {
        allMarkers.push({
          ...result,
          latitude: loc.latitude,
          longitude: loc.longitude,
          location_name: loc.name,
          marker_id: `${result.id || result.url}-${idx}`
        });
      });
    } else if (result.latitude && result.longitude) {
      allMarkers.push({
        ...result,
        marker_id: result.id || result.url
      });
    }
  });
  
  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.95)',
      borderRadius: 12,
      padding: 12,
      marginBottom: 12,
      border: '1px solid rgba(251, 191, 36, 0.3)'
    }}>
      <div style={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8
      }}>
        <div style={{ 
          fontSize: '0.75rem', 
          color: '#a1a1aa', 
          fontWeight: 600 
        }}>
          🗺️ Interactive Map
        </div>
        <div style={{
          fontSize: '0.65rem',
          color: '#6b7280',
          background: 'rgba(255,255,255,0.1)',
          padding: '3px 8px',
          borderRadius: 10
        }}>
          {allMarkers.length} locations
        </div>
      </div>
      
      <div style={{ 
        borderRadius: 8, 
        overflow: 'hidden',
        height: height
      }}>
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {allMarkers.map((result, idx) => (
            <CircleMarker
              key={result.marker_id || idx}
              center={[result.latitude, result.longitude]}
              radius={8}
              fillColor={getMarkerColor(result)}
              fillOpacity={0.8}
              color="#fff"
              weight={2}
              eventHandlers={{
                click: () => onMarkerClick && onMarkerClick(result)
              }}
            >
              <Popup>
                <div style={{ maxWidth: 200 }}>
                  <strong style={{ fontSize: '0.8rem' }}>
                    {result.location_name || 'Unknown Location'}
                  </strong>
                  <p style={{ fontSize: '0.7rem', margin: '4px 0', color: '#666' }}>
                    {result.title?.substring(0, 80)}...
                  </p>
                  <a 
                    href={result.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.65rem', color: '#3b82f6' }}
                  >
                    View Source
                  </a>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
      
      {/* Legend */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 8,
        justifyContent: 'center'
      }}>
        {[
          { color: '#3b82f6', label: 'News' },
          { color: '#10b981', label: 'Blog' },
          { color: '#047857', label: 'Social' },
          { color: '#ef4444', label: 'Video' },
          { color: '#FDE047', label: 'Academic' },
          { color: '#047857', label: 'Other' }
        ].map(item => (
          <div key={item.label} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 4,
            fontSize: '0.6rem',
            color: '#9ca3af'
          }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: item.color
            }} />
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * SlimResultRow - Extremely thin result row for worldwide/compact view
 */
export const SlimResultRow = ({ result, onClick }) => {
  return (
    <div
      onClick={() => onClick && onClick(result)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 10px',
        background: 'rgba(30, 41, 59, 0.6)',
        borderRadius: 4,
        marginBottom: 2,
        cursor: 'pointer',
        transition: 'background 0.2s',
        borderLeft: `3px solid ${
          result.article_type?.toLowerCase().includes('news') ? '#3b82f6' :
          result.article_type?.toLowerCase().includes('blog') ? '#10b981' :
          result.article_type?.toLowerCase().includes('social') ? '#FEF08A' :
          '#fbbf24'
        }`
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(245, 158, 11, 0.1)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(30, 41, 59, 0.6)'}
    >
      {/* Location indicator */}
      {result.latitude && (
        <span style={{ fontSize: '0.65rem' }}>📍</span>
      )}
      
      {/* Title - truncated */}
      <span style={{
        flex: 1,
        fontSize: '0.7rem',
        color: '#334155',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {result.title}
      </span>
      
      {/* Domain */}
      <span style={{
        fontSize: '0.6rem',
        color: '#6b7280',
        flexShrink: 0
      }}>
        {result.root_domain?.replace('www.', '')}
      </span>
      
      {/* External link */}
      <a
        href={result.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontSize: '0.6rem',
          color: '#3b82f6',
          textDecoration: 'none'
        }}
      >
        ↗
      </a>
    </div>
  );
};

/**
 * FullControlsPanel - All controls combined for chat rooms
 */
export const FullControlsPanel = ({ 
  token,
  categories = [],
  selectedCategories = [],
  onCategoryChange,
  databaseMode = 'personal',
  onDatabaseChange,
  contentFilters = ['all'],
  onFilterChange,
  searchResults = [],
  onResultClick,
  showMap = true
}) => {
  const [users, setUsers] = useState([]);
  
  // Fetch users for individual mode
  useEffect(() => {
    if (token && databaseMode === 'individual') {
      get('/users/public-profiles')
        .then(data => setUsers(data.users || data || []))
        .catch(() => setUsers([]));
    }
  }, [token, databaseMode, get]);
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 8
    }}>
      {/* Database Toggle */}
      <DatabaseToggle
        value={databaseMode}
        onChange={onDatabaseChange}
        userList={users}
        onUserSelect={(userId) => console.log('Selected user:', userId)}
      />
      
      {/* Category Checkboxes */}
      {categories.length > 0 && (
        <CategoryCheckboxes
          categories={categories}
          selectedIds={selectedCategories}
          onChange={onCategoryChange}
        />
      )}
      
      {/* URL Preference Filters */}
      <URLPreferenceFilters
        filters={contentFilters}
        onChange={onFilterChange}
      />
      
      {/* Interactive Map */}
      {showMap && (
        <InteractiveMapPanel
          results={searchResults}
          onMarkerClick={onResultClick}
          height={250}
        />
      )}
      
      {/* Results List (slim rows for worldwide) */}
      {databaseMode === 'worldwide' && searchResults.length > 0 && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.95)',
          borderRadius: 12,
          padding: 12,
          border: '1px solid rgba(253, 224, 71, 0.3)',
          maxHeight: 300,
          overflowY: 'auto'
        }}>
          <div style={{ 
            fontSize: '0.75rem', 
            color: '#a1a1aa', 
            marginBottom: 8,
            fontWeight: 600 
          }}>
            Worldwide Results ({searchResults.length})
          </div>
          {searchResults.map((result, idx) => (
            <SlimResultRow
              key={result.id || idx}
              result={result}
              onClick={onResultClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default {
  DatabaseToggle,
  CategoryCheckboxes,
  URLPreferenceFilters,
  InteractiveMapPanel,
  SlimResultRow,
  FullControlsPanel
};
